/**
 * "N people are on this puzzle right now" (ADR 0038).
 *
 * Two topics, because a tab reads the number from the moment the page opens but only counts
 * once it is really playing: `solvers:{slug}` feeds the count, `solving:{slug}` declares
 * this tab as one of the people counted — subscribing is the declaration, and the socket's
 * own keepalive is what holds it. The number that comes back includes me, so the hook hands
 * back the others. Like presence, it has no HTTP fallback: no socket, no count.
 */
import { useState } from "react";
import { useRealtimeOpen, useTopic } from "@/core/realtime/RealtimeProvider";
import { solversTopic, solvingTopic } from "@/core/realtime/protocol";

/** The number of *other* people solving `slug`, or null while nothing is known. */
export function useSolverCount(slug: string, solving: boolean): number | null {
  const live = useRealtimeOpen();
  const [count, setCount] = useState<number | null>(null);

  useTopic(slug ? solversTopic(slug) : null, (frame) => {
    if (frame.type === "solvers.count") setCount(frame.data.count);
  });
  // Nothing to listen to here: the subscription itself is the message.
  useTopic(slug && solving ? solvingTopic(slug) : null, () => undefined);

  if (!live || count === null) return null;
  return Math.max(0, count - (solving ? 1 : 0));
}
